import type {
  CatalogBrandDto,
  CatalogCategoryDto,
  CatalogPricelistDto,
  PricelistGridDto
} from "../api/schemas";

export interface QuickPricelistDefinition {
  id: string;
  label: string;
  brand: string;
  category: string;
  displayNameTerms: readonly string[];
}

export interface ResolvedQuickPricelist {
  brand: string;
  category: string;
  key: string;
  pricelist: CatalogPricelistDto;
}

export const quickPricelistDefinitions: readonly QuickPricelistDefinition[] = [
  {
    id: "kei-al-armoured",
    label: "Al Armd",
    brand: "kei",
    category: "Al Armd",
    displayNameTerms: ["latest"]
  },
  {
    id: "kei-cu-armoured",
    label: "Cu Armd",
    brand: "kei",
    category: "Cu Armd",
    displayNameTerms: ["latest"]
  },
  {
    id: "kei-fr-single-core",
    label: "FR 1C",
    brand: "kei",
    category: "FR Flexible",
    displayNameTerms: ["latest", "single core"]
  },
  {
    id: "kei-fr-multicore",
    label: "FR Multi",
    brand: "kei",
    category: "FR Flexible",
    displayNameTerms: ["latest", "multicore"]
  },
  {
    id: "kei-frls-single-core",
    label: "FRLS 1C",
    brand: "kei",
    category: "FRLS Flexible",
    displayNameTerms: ["latest", "single core"]
  },
  {
    id: "kei-frls-multicore",
    label: "FRLS Multi",
    brand: "kei",
    category: "FRLS Flexible",
    displayNameTerms: ["latest", "multicore"]
  }
];

export function findBrand(
  brands: CatalogBrandDto[] | undefined,
  selectedBrand: string
): CatalogBrandDto | undefined {
  return brands?.find((brand) => brand.brand === selectedBrand) ?? brands?.[0];
}

export function findCategory(
  brand: CatalogBrandDto | undefined,
  selectedCategory: string
): CatalogCategoryDto | undefined {
  return (
    brand?.categories.find((category) => category.category === selectedCategory) ??
    brand?.categories[0]
  );
}

export function findPricelist(
  category: CatalogCategoryDto | undefined,
  selectedKey: string
): CatalogPricelistDto | undefined {
  return (
    category?.pricelists.find(
      (pricelist) => pricelistSelectionKey(pricelist) === selectedKey
    ) ??
    category?.pricelists.find((pricelist) => pricelist.tag === selectedKey) ??
    category?.pricelists[0]
  );
}

export function findGrid(
  grids: PricelistGridDto[] | undefined,
  brand: string,
  category: string,
  pricelist: CatalogPricelistDto | undefined
): PricelistGridDto | undefined {
  if (!pricelist) {
    return undefined;
  }

  return grids?.find(
    (grid) =>
      grid.brand === brand &&
      grid.category === category &&
      grid.tag === pricelist.tag &&
      grid.display_name === pricelist.display_name
  );
}

export function resolveQuickPricelist(
  brands: CatalogBrandDto[] | undefined,
  definition: QuickPricelistDefinition
): ResolvedQuickPricelist | undefined {
  const brand = brands?.find(
    (candidate) => normalizeCatalogText(candidate.brand) === normalizeCatalogText(definition.brand)
  );
  const category = brand?.categories.find(
    (candidate) =>
      normalizeCatalogText(candidate.category) === normalizeCatalogText(definition.category)
  );
  const pricelist = category?.pricelists.find((candidate) => {
    const displayName = normalizeCatalogText(candidate.display_name);
    return definition.displayNameTerms.every((term) =>
      includesCatalogTerm(displayName, normalizeCatalogText(term))
    );
  });

  if (!brand || !category || !pricelist) {
    return undefined;
  }

  return {
    brand: brand.brand,
    category: category.category,
    key: pricelistSelectionKey(pricelist),
    pricelist
  };
}

export function pricelistSelectionKey(pricelist: CatalogPricelistDto): string {
  return `${pricelist.tag}::${pricelist.display_name}`;
}

function normalizeCatalogText(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function includesCatalogTerm(value: string, term: string): boolean {
  return ` ${value} `.includes(` ${term} `);
}
